<template>
  <DetailCard>
    <template #heading>
      <span class="pi pi-file-check"></span>
      {{ $t('messages.detail.reportQc.title') }}
    </template>

    <div class="p-4" data-testid="report-qc">
      <!--
        RED-373: the latest run and the way to it. Shown whether or not the file
        is still here — the findings outlive the .docx (design-spec §10.3).
      -->
      <div
        v-if="check"
        class="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 border-b border-gray-100 pb-3"
        data-testid="report-qc-latest-check"
        :data-status="check.status"
      >
        <div>
          <p class="text-xs uppercase tracking-wide text-gray-500 mb-1">
            {{ $t('messages.detail.reportQc.card.latest') }}
          </p>
          <p class="text-sm text-gray-700" data-testid="report-qc-latest-status">
            {{ checkStatusText }}<template v-if="check.status === 'completed' && Number.isInteger(check.findingCount)">
              · {{ $t('messages.detail.reportQc.card.findings', check.findingCount) }}</template>
          </p>
        </div>
        <Link
          :href="route('evaluation.report-check.panel', objectId)"
          class="text-sm text-blue-600 underline underline-offset-2 hover:text-blue-800"
          data-testid="report-qc-open-check"
        >
          {{ $t('messages.detail.reportQc.card.open') }}
        </Link>
      </div>

      <!-- file-ready: the trust panel -->
      <div v-if="upload" data-testid="report-qc-trust-panel">
        <p class="text-xs uppercase tracking-wide text-gray-500 mb-2">
          {{ $t('messages.detail.reportQc.panel.label') }}
        </p>
        <div class="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span class="font-medium break-all" data-testid="report-qc-filename">{{ upload.originalFilename }}</span>
          <span class="text-sm text-gray-600">{{ megabytes(upload.byteSize) }} MB</span>
          <span class="text-sm text-gray-600">{{ clock(upload.uploadedAt) }}</span>
        </div>
        <p class="mt-2" data-testid="report-qc-read-summary">
          {{
            $t('messages.detail.reportQc.panel.read', {
              tables: integer(upload.tableCount),
              chars: integer(upload.charCount),
            })
          }}
        </p>
        <Message
          v-if="upload.tableCount === 0"
          severity="warn"
          :closable="false"
          class="mt-3"
          data-testid="report-qc-no-tables"
        >
          {{ $t('messages.detail.reportQc.panel.noTables') }}
        </Message>
        <p class="mt-2 text-sm text-gray-600" data-testid="report-qc-deletes-at">
          {{
            $t('messages.detail.reportQc.panel.deletesAt', {
              date: day(upload.deletesAt),
              time: clock(upload.deletesAt),
            })
          }}
        </p>
        <!-- RED-373: a hint, never a block — the check runs either way. -->
        <QcNotice
          v-if="upload.subjectMismatch"
          tone="warn"
          icon="pi-exclamation-triangle"
          class="mt-3"
          data-testid="report-qc-subject-mismatch"
        >
          <strong class="font-semibold text-gray-700">{{ $t('messages.evaluation.qualityCheck.subject.title') }}</strong>
          {{ $t('messages.evaluation.qualityCheck.subject.body') }}
        </QcNotice>
        <div v-if="rejectionText" data-testid="report-qc-rejection">
          <Message severity="error" :closable="false" class="mt-3">
            {{ rejectionText }}
          </Message>
        </div>
        <!-- The run starts on the quality-check screen (the link above), behind its cost dialog. -->

        <!--
          Replacing is a first-class action, not "remove, then upload again".
          The server already supersedes in a transaction, so the current file stays
          readable until the new one has landed — spec chapter 5, "nothing lost".
          Without a chooser here the only way to swap a file would be to delete the
          good one first and hope the next upload succeeds.
        -->
        <div v-if="uploading" class="mt-4" data-testid="report-qc-progress">
          <ProgressBar :value="progressPercent" :show-value="true">
            {{ megabytes(loadedBytes) }} / {{ megabytes(totalBytes) }} MB
          </ProgressBar>
          <Button
            :label="$t('messages.detail.reportQc.cancel')"
            severity="secondary"
            text
            size="small"
            class="mt-2"
            data-testid="report-qc-cancel"
            @click="cancel"
          />
        </div>

        <div v-else-if="reading" class="mt-4 flex items-center gap-2 text-sm" data-testid="report-qc-reading">
          <ProgressSpinner class="w-5 h-5" stroke-width="6" />
          {{ $t('messages.detail.reportQc.reading') }}
        </div>

        <div v-else class="flex flex-wrap justify-between items-center gap-2 mt-4">
          <div data-testid="report-qc-replace">
            <FileUpload
              ref="fileUpload"
              mode="basic"
              name="file"
              :url="storeUrl"
              :choose-label="$t('messages.detail.reportQc.panel.replace')"
              choose-icon="pi pi-upload"
              :auto="true"
              custom-upload
              @uploader="onUploader"
            />
          </div>
          <Button
            :label="$t('messages.detail.reportQc.panel.remove')"
            icon="pi pi-trash"
            severity="secondary"
            text
            size="small"
            :loading="removing"
            data-testid="report-qc-remove"
            @click="remove"
          />
        </div>
      </div>

      <!-- no-file / uploading / reading / rejected -->
      <div v-else>
        <p class="font-medium">{{ $t('messages.detail.reportQc.dropzone.title') }}</p>
        <p class="text-sm text-gray-600 mt-1">{{ $t('messages.detail.reportQc.dropzone.which') }}</p>
        <p class="text-sm text-gray-600" data-testid="report-qc-retention-note">
          {{ $t('messages.detail.reportQc.dropzone.retention', { days: retentionDaysMax ?? '' }) }}
        </p>

        <div v-if="rejectionText" data-testid="report-qc-rejection">
          <Message severity="error" :closable="false" class="mt-3">
            {{ rejectionText }}
          </Message>
        </div>

        <div v-if="uploading" class="mt-3" data-testid="report-qc-progress">
          <ProgressBar :value="progressPercent" :show-value="true">
            {{ megabytes(loadedBytes) }} / {{ megabytes(totalBytes) }} MB
          </ProgressBar>
          <Button
            :label="$t('messages.detail.reportQc.cancel')"
            severity="secondary"
            text
            size="small"
            class="mt-2"
            data-testid="report-qc-cancel"
            @click="cancel"
          />
        </div>

        <div v-else-if="reading" class="mt-3 flex items-center gap-2 text-sm" data-testid="report-qc-reading">
          <ProgressSpinner class="w-5 h-5" stroke-width="6" />
          {{ $t('messages.detail.reportQc.reading') }}
        </div>

        <!--
          Deliberately NO `accept` on the FileUpload. PrimeVue validates `accept`
          itself and renders its own English message
          ("Invalid file type, allowed file types: ..."), which pre-empts the five
          Estonian rejection sentences of the upload-step spec (chapter 5) — a PDF
          would never reach `onUploader`, so the appraiser would never be told, in
          Estonian, WHY a PDF cannot be checked. Every file therefore reaches our
          handler; the server decides the type and returns the code the composable
          maps to a sentence.
        -->
        <div v-else class="mt-3" data-testid="report-qc-dropzone">
          <FileUpload
            ref="fileUpload"
            mode="basic"
            name="file"
            :url="storeUrl"
            :choose-label="$t('messages.detail.reportQc.dropzone.choose')"
            choose-icon="pi pi-upload"
            :auto="true"
            custom-upload
            @uploader="onUploader"
          />
        </div>
      </div>
    </div>
  </DetailCard>
</template>

<script setup>
import { Link, usePage } from '@inertiajs/vue3'
import { format, parseISO } from 'date-fns'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import QcNotice from '@/components/evaluation/qc/QcNotice.vue'
import { useReportUpload } from '@/composables/useReportUpload'
import DetailCard from '../DetailCard.vue'

const props = defineProps({
  objectId: { type: [String, Number], required: true },
})

const { t } = useI18n()

const {
  upload,
  retentionDaysMax,
  uploading,
  reading,
  removing,
  loadedBytes,
  totalBytes,
  rejection,
  send,
  cancel,
  remove,
} = useReportUpload(props.objectId)

const fileUpload = ref(null)

const page = usePage()

/** RED-373: the latest run, owner-scoped on the server (`reportQcFor`). */
const check = computed(() => page.props.reportQc?.check ?? null)

const checkStatusText = computed(() => {
  const status = check.value?.status
  return ['queued', 'processing', 'completed', 'failed'].includes(status)
    ? t(`messages.detail.reportQc.card.status.${status}`)
    : ''
})

const storeUrl = computed(() => route('evaluation.report-upload.store', props.objectId))

const progressPercent = computed(() =>
  totalBytes.value > 0 ? Math.min(100, Math.round((loadedBytes.value * 100) / totalBytes.value)) : 0,
)

const rejectionText = computed(() => {
  if (!rejection.value) {
    return null
  }
  const key = {
    pdf: 'pdf',
    doc: 'doc',
    unsupported: 'unsupported',
    too_large: 'tooLarge',
    unreadable: 'unreadable',
    csrfExpired: 'csrfExpired',
  }[rejection.value.code] ?? 'generic'

  return t(`messages.detail.reportQc.errors.${key}`, { max: rejection.value.maxMegabytes ?? '' })
})

const onUploader = async event => {
  const file = Array.from(event.files ?? [])[0]
  // PrimeVue keeps the chosen file in its own state; clear it so a rejection
  // never leaves a dead chooser behind (the RED-334 lesson).
  fileUpload.value?.clear()
  if (!file) {
    return
  }
  await send(file)
}

const integer = value => new Intl.NumberFormat('et-EE').format(Number(value ?? 0))

const megabytes = bytes => (Number(bytes ?? 0) / (1024 * 1024)).toFixed(1).replace('.', ',')

const day = iso => (iso ? format(parseISO(iso), 'dd.MM.yyyy') : '')

const clock = iso => (iso ? format(parseISO(iso), 'HH:mm') : '')
</script>
